import { useEffect, useMemo, useState } from "react";
import z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Modal, CheckboxMultiSelect } from "@/components";
import { useShopStore } from "@/store/shopStore";
import { useGroupStore } from "@/store/groupStore";
import { toastError, toastSuccess } from "@/utils/toastify";
import {
  ApiResponse,
  DEFAULT_SUCCESS_MESSAGE,
  GetStoresResp,
  RoleEnum,
} from "@/types";
import { useCompanyStore } from "@/store/companyStore";
import { useLoadingStore } from "@/store/loadingStore";
import { REQUIRED_MESSAGE } from "../company/schema";
import { useAuth } from "@/contexts/AuthContext";

type CreateGroupFormData = {
  name: string;
  stores?: number[];
};

const FIRST_PAGE = 1;

const schemaGroup = z.object({
  name: z.string().trim().min(1, REQUIRED_MESSAGE),
  stores: z.array(z.number()).optional(),
});

export function CreateGroup() {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { role } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setError,
  } = useForm<CreateGroupFormData>({
    resolver: zodResolver(schemaGroup),
  });
  const { createGroup, fetchGroupsInCompany, setGroupsInCompany } =
    useGroupStore();
  const { shopsInCompany, fetchShopsInCompany, setShopsInCompany } =
    useShopStore();
  const { company, stores, setStores } = useCompanyStore();
  const { setLoading: setLoadingCreateGroup, isLoading: loadingCreateGroup } =
    useLoadingStore();
  const companyId = Number(company?.id);

  const loadOptions = async (page: number) => {
    if (isLoading || !companyId) return;
    setIsLoading(true);
    const response = await fetchShopsInCompany({
      companyId: companyId,
      page: page,
      isPagination: 0, // TODO: Can update infinite scroll
    });
    const activeOptions = [
      ...(page == FIRST_PAGE ? [] : shopsInCompany),
      ...response.data.map((item) => ({ label: item.label, value: item.key })),
    ];
    setShopsInCompany(activeOptions);
    setHasMore(false); // TODO: Can update infinite scroll
    setIsLoading(false);
  };

  useEffect(() => {
    if (!open) return;
    setPage(FIRST_PAGE);
    setShopsInCompany([]);
    loadOptions(FIRST_PAGE);
  }, [open]);

  const handleScrollToBottom = () => {
    if (hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadOptions(nextPage);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const onSubmit = async (data: CreateGroupFormData) => {
    const tempStores = stores;
    try {
      setLoadingCreateGroup(true);
      const storeIds = new Set(data.stores || []);
      const resp = await createGroup({
        name: data.name?.trim(),
        storeIds: data.stores || [],
      });

      // update list stores in company detail for select
      const newStores = {
        ...stores,
        data: (stores?.data || [])?.map((i) => {
          if (storeIds.has(i.id)) {
            return {
              ...i,
              group: {
                id: resp?.data?.id || 0,
                name: resp?.data?.name || "",
              },
            };
          }
          return i;
        }),
      } as unknown as ApiResponse<GetStoresResp[]>;
      setStores(newStores);
      const response = await fetchGroupsInCompany(
        role || RoleEnum.HEAD_OFFICE_USER,
        {
          companyId: companyId,
          page: FIRST_PAGE,
          limit: 20,
          isPagination: 0, // update loading inscroll sang load hết
        }
      );
      setGroupsInCompany({
        ...response,
        data: response.data.map((item) => ({
          label: item.name,
          value: item.id,
        })),
      });
      handleClose();
      setLoadingCreateGroup(false);
      toastSuccess(resp?.message || DEFAULT_SUCCESS_MESSAGE);
      reset();
    } catch (error: any) {
      setLoadingCreateGroup(false);
      // back lại dữ liệu cũ khi có lỗi
      setStores(tempStores);
      // set lỗi cho form từ server
      if (error) {
        if (error?.error === "name") {
          setError("name", { message: error.message });
        } else {
          toastError(error?.message);
        }
      }
    }
  };

  // Tạo Map để lookup O(1) thay vì includes O(n)
  const storeMap = useMemo(() => {
    return new Map(shopsInCompany.map((store) => [store.value, store]));
  }, [shopsInCompany]);

  const storeDisplayName = useMemo(() => {
    return company?.store_display_name || "店舗";
  }, [company]);

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleOpen}
        disabled={!companyId || company?.status == "DISABLE"}
      >
        グループ作成
      </Button>
      {/* new group */}
      <Modal
        title="グループ作成"
        open={open}
        overflowScroll={false}
        onOpenChange={loadingCreateGroup ? () => {} : setOpen}
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-8">
            <Input
              label="グループ名"
              required
              placeholder="グループ名を入力してください。"
              {...register("name")}
              disabled={loadingCreateGroup}
              error={errors.name?.message}
              maxLength={150}
            />
            <Controller
              control={control}
              name="stores"
              render={({ field: { onChange, value } }) => {
                const selectedStores =
                  value && value?.length > 0
                    ? value
                        .map((storeValue) => storeMap.get(storeValue))
                        .filter(Boolean)
                    : [];
                return (
                  <CheckboxMultiSelect
                    label={`所属${storeDisplayName}`}
                    placeholder={`所属${storeDisplayName}を選択してください。`}
                    height="2.75rem"
                    isSearchable={true}
                    isClearable={true}
                    isLoading={isLoading}
                    options={shopsInCompany}
                    value={selectedStores as any}
                    isDisabled={loadingCreateGroup}
                    onChange={(data) => {
                      const values = data ? data.map((item) => item.value) : [];
                      onChange(values);
                    }}
                    onMenuScrollToBottom={handleScrollToBottom}
                  />
                );
              }}
            />
          </div>
          <div className="flex justify-end gap-4 mt-4">
            <Button
              buttonType="text"
              className="!h-12"
              type="button"
              onClick={handleClose}
              disabled={loadingCreateGroup}
            >
              キャンセル
            </Button>

            <Button
              buttonType="primary"
              className="!h-12"
              type="submit"
              disabled={!!errors.name}
              isLoading={loadingCreateGroup}
            >
              作成
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
